import './style.scss'
import { Button, Popover } from 'antd'
import dayjs from 'dayjs'
import { FC, memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CheckLogin from '@/components/CheckLogin'
import PayPointModal from '@/components/PayPointModal'
import VipManagementModal from '@/components/VipManagementModal'
import { UserStore } from '@/global-states'
import { eventTracking } from '@/libs/util'
import { InfoCircleOutlined } from '@ant-design/icons'

interface IProps {
  onSub: () => void
}

const UserDetail: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['user', 'system'])
  const { onSub } = props
  const { phone, userPackage } = UserStore
  const [payPointModalOpen, setPayPointModalOpen] = useState(false)
  const [vipManagementModalOpen, setVipManagementModalOpen] = useState(false)

  const vip = useMemo(() => {
    return !!userPackage?.membership_expire_time
  }, [userPackage])

  const vipExpired = useMemo(() => {
    return !!userPackage?.membership_expired
  }, [userPackage])

  const totalPoint = useMemo(() => {
    if (!userPackage) return 0
    return (
      (userPackage.membership_credits || 0) + (userPackage.permanent_credits || 0) + (userPackage.free_credits || 0)
    )
  }, [userPackage])

  const toPayPoint = () => {
    eventTracking('RechargeCredits')
    setPayPointModalOpen(true)
  }

  const freeToPayPointExpired = () => {
    eventTracking('ToPayPointLimit')
    setPayPointModalOpen(true)
  }

  const toPayPointExpired = () => {
    eventTracking('ToPayPointExpired')
    setPayPointModalOpen(true)
  }

  return (
    <div className="setting-user-detail">
      <div className="header-user">
        <div className="user">
          <div className="name">{translate('personal.greeting')}</div>

          {phone ? (
            <div className="user-info">
              <div className="phone">
                <label>{phone}</label>
                {vip && !vipExpired && (
                  <label className="gradient" onClick={() => setVipManagementModalOpen(true)}>
                    {translate('personal.vip_management')}
                  </label>
                )}
              </div>
              {vip ? (
                <>
                  {vipExpired ? (
                    <label className="text red">
                      {translate('personal.package_expired', {
                        date: dayjs(userPackage.membership_expire_time * 1000).format('YYYY-MM-DD HH:mm')
                      })}
                    </label>
                  ) : (
                    <label className="text">
                      {translate('personal.expiry_time', {
                        date: dayjs(userPackage.membership_expire_time * 1000).format('YYYY-MM-DD HH:mm')
                      })}
                    </label>
                  )}
                </>
              ) : (
                <label className="text">{translate('personal.no_membership')}</label>
              )}
            </div>
          ) : (
            <div className="user-info">
              <label className="text">{translate('personal.not_logged_in')}</label>
            </div>
          )}
        </div>
        {vip ? (
          <div className="btns">
            <Button
              type="primary"
              onClick={() => {
                eventTracking('RenewUpgrade')
                onSub()
              }}
            >
              {vipExpired ? translate('personal.renew_now') : translate('personal.renew_upgrade')}
            </Button>
          </div>
        ) : (
          <div className="btns">
            <CheckLogin>
              <Button
                type="primary"
                onClick={() => {
                  eventTracking('SubscribeNow')
                  onSub()
                }}
              >
                {translate('personal.subscribe_now')}
              </Button>
            </CheckLogin>
          </div>
        )}
      </div>
      <div className="header-desc">
        <div className="left">
          <label className="text">{translate('personal.points_balance')}</label>
          <label className="num">{totalPoint}</label>
          <label className="icon">
            <Popover
              content={
                <div className="point-popover">
                  <div>
                    <label className="label">{translate('personal.free_points')}:</label>
                    <label className="num gradient">{userPackage?.free_credits || 0}</label>
                  </div>
                  <div>
                    <label className="label">{translate('personal.membership_points')}:</label>
                    <label className="num gradient">{userPackage?.membership_credits || 0}</label>
                  </div>
                  <div>
                    <label className="label">{translate('personal.purchased_points')}:</label>
                    <label className="num gradient">{userPackage?.permanent_credits || 0}</label>
                  </div>

                  <a
                    target="_blank"
                    className="link gradient"
                    href="https://lingverse.feishu.cn/sheets/IJeQsahvdhqX5btBSzycBKPRnr0"
                    rel="noreferrer"
                  >
                    {translate('personal.view_points_guide')}
                  </a>
                </div>
              }
              placement="right"
            >
              <InfoCircleOutlined />
            </Popover>
          </label>
        </div>
        <div className="right">
          {vip ? (
            <>
              {vipExpired ? (
                <Button onClick={toPayPointExpired}>{translate('personal.recharge_points')}</Button>
              ) : (
                <Button className="point-pay" onClick={toPayPoint}>
                  {translate('personal.recharge_points')}
                  <div className="tag">{translate('personal.special_offer')}</div>
                </Button>
              )}
            </>
          ) : (
            <CheckLogin>
              <Button onClick={freeToPayPointExpired}>{translate('personal.recharge_points')}</Button>
            </CheckLogin>
          )}
        </div>
      </div>

      <VipManagementModal open={vipManagementModalOpen} onCancel={() => setVipManagementModalOpen(false)} />

      <PayPointModal open={payPointModalOpen} onCancel={() => setPayPointModalOpen(false)} />
    </div>
  )
}

export default memo(UserDetail)
